import React, { useState, useEffect, useMemo } from 'react'; // Добавлен useState
import {
  Card, CardContent, Typography, Grid, Chip, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Box, CircularProgress, List, ListItem, ListItemText, TableSortLabel,
  Collapse, FormControl, FormLabel, TextField
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';

import { useRecoilValue, useSetRecoilState } from 'recoil';
import {
  optimalWavesState,
  simpleStatsState,
  sequenceStatsState,
  waveClusterStatsState,
  waveClusterOutcomesState,
  clusterExpectancyState,
} from '#state/trades';
import { strategiesConfig } from '#state/settings';
import {
  calculateFadeExpectancy,
  getBlendedExpectancy,
  calculateClusterFadeExpectancy,
  getClustersPaths,
  getGroupedOutcomes,
} from '#state/utils';
import { getSpecificClusterStats, getClustersOutcome, predictSequence } from './charts/CandleUtils'

// const defaultFee = 0.001 // 0.00065 // 0.00026
const marketFee = 0.0005 // 0.0005
const limitFee = 0.0002 // 0.0002
const cashbackRate = 0.35

function formatValue(num, significantDigits = 3, isPercent = false) {
  // ... (ваш код formatValue без изменений)
  if (typeof num !== 'number' || isNaN(num)) return 'N/A';
  if (isPercent) num = num * 100
  const suffixes = { 1000: 'K', 1000000: 'M', 1000000000: 'B', 1000000000000: 'T' };
  const absNum = Math.abs(num);
  const sigDelta = Math.max(0, significantDigits - 3)
  if (absNum >= 1000) {
    let scale = 1;
    let suffix = '';
    let scaledNum = num;
    while (scaledNum >= 1000 && scale <= 1e12) {
      scale *= 1000;
      scaledNum = num / scale;
      suffix = suffixes[scale] || '';
    }
    if (scaledNum >= 100) { return (scaledNum.toFixed(sigDelta) + suffix); }
    else if (scaledNum >= 10) { return (scaledNum.toFixed(sigDelta + 1) + suffix); }
    else { return (scaledNum.toFixed(sigDelta + 2) + suffix); }
  } else if (absNum < 1) {
    return num.toPrecision(significantDigits);
  } else {
    if (num >= 100) { return num.toFixed(sigDelta); }
    else if (num >= 10) { return num.toFixed(sigDelta + 1); }
    else { return num.toFixed(sigDelta + 2); }
  }
}

export const columnsConfig = [
  {
    id: 'name',
    label: 'Category',
    align: 'left',
    formatter: (data, rowConfig) => <strong>{rowConfig.label}</strong>,
  },
  {
    id: 'count',
    label: 'Count',
    align: 'right',
    formatter: (data) => data?.count,
  },
  {
    id: 'avgDeltaPercent',
    label: 'Avg Delta (%)',
    align: 'right',
    formatter: (data) => `${formatValue(data?.deltaPercent?.avg, 2, true)}%`,
  },
  {
    id: 'medDeltaPercent',
    label: 'Med Delta (%)',
    align: 'right',
    formatter: (data) => `${formatValue(data?.deltaPercent?.median, 2, true)}%`,
  },
  {
    id: 'avgDeltaPRatio',
    label: 'Avg Delta (%) Ratio',
    align: 'right',
    formatter: (data) => formatValue(data?.deltaRatioPrev?.avg, 2),
  },
  {
    id: 'medDeltaPRatio',
    label: 'Med Delta (%) Ratio',
    align: 'right',
    formatter: (data) => formatValue(data?.deltaRatioPrev?.median, 2),
  },
  {
    id: 'avgVolume',
    label: 'Avg Volume',
    align: 'right',
    formatter: (data) => formatValue(data?.totalVolume?.avg, 3),
  },
];

export const rowsConfig = [
  // --- General Stats ---
  { id: 'overall', label: 'Overall', level: 0, dataAccessor: (stats) => stats.overall },
  { id: 'overallUp', label: '↳ Up', level: 1, dataAccessor: (stats) => stats.overall?.up },
  { id: 'overallDown', label: '↳ Down', level: 1, dataAccessor: (stats) => stats.overall?.down },

  // --- Move Waves ---
  { id: 'move', label: 'Move', level: 0, dataAccessor: (stats) => stats.move },
  { id: 'moveUp', label: '↳ Up', level: 1, dataAccessor: (stats) => stats.move?.up },
  { id: 'moveDown', label: '↳ Down', level: 1, dataAccessor: (stats) => stats.move?.down },

  // --- Pullback Waves ---
  { id: 'pullback', label: 'Pullback', level: 0, dataAccessor: (stats) => stats.pullback },
  { id: 'pullbackUp', label: '↳ Up', level: 1, dataAccessor: (stats) => stats.pullback?.up },
  { id: 'pullbackDown', label: '↳ Down', level: 1, dataAccessor: (stats) => stats.pullback?.down },

  // --- "Pre" Sequences (Describes the wave BEFORE the event) ---
  { id: 'pullbackPreMove', label: 'Pre: Pullback before Move', level: 0, dataAccessor: (stats) => stats.sequences?.pullbackPreMove },
  { id: 'pullbackPreMoveUp', label: '↳ Up', level: 1, dataAccessor: (stats) => stats.sequences?.pullbackPreMove?.up },
  { id: 'pullbackPreMoveDown', label: '↳ Down', level: 1, dataAccessor: (stats) => stats.sequences?.pullbackPreMove?.down },

  { id: 'movePrePullback', label: 'Pre: Move before Pullback', level: 0, dataAccessor: (stats) => stats.sequences?.movePrePullback },
  { id: 'movePrePullbackUp', label: '↳ Up', level: 1, dataAccessor: (stats) => stats.sequences?.movePrePullback?.up },
  { id: 'movePrePullbackDown', label: '↳ Down', level: 1, dataAccessor: (stats) => stats.sequences?.movePrePullback?.down },

  { id: 'pullbackPrePullback', label: 'Pre: Pullback before Pullback', level: 0, dataAccessor: (stats) => stats.sequences?.pullbackPrePullback },
  { id: 'pullbackPrePullbackUp', label: '↳ Up', level: 1, dataAccessor: (stats) => stats.sequences?.pullbackPrePullback?.up },
  { id: 'pullbackPrePullbackDown', label: '↳ Down', level: 1, dataAccessor: (stats) => stats.sequences?.pullbackPrePullback?.down },

  { id: 'movePreMove', label: 'Pre: Move before Move', level: 0, dataAccessor: (stats) => stats.sequences?.movePreMove },
  { id: 'movePreMoveUp', label: '↳ Up', level: 1, dataAccessor: (stats) => stats.sequences?.movePreMove?.up },
  { id: 'movePreMoveDown', label: '↳ Down', level: 1, dataAccessor: (stats) => stats.sequences?.movePreMove?.down },

  // --- "Post" Sequences (Describes the wave AFTER the event) ---
  { id: 'movePostPullback', label: 'Post: Move after Pullback', level: 0, dataAccessor: (stats) => stats.sequences?.movePostPullback },
  { id: 'movePostPullbackUp', label: '↳ Up', level: 1, dataAccessor: (stats) => stats.sequences?.movePostPullback?.up },
  { id: 'movePostPullbackDown', label: '↳ Down', level: 1, dataAccessor: (stats) => stats.sequences?.movePostPullback?.down },

  { id: 'pullbackPostMove', label: 'Post: Pullback after Move', level: 0, dataAccessor: (stats) => stats.sequences?.pullbackPostMove },
  { id: 'pullbackPostMoveUp', label: '↳ Up', level: 1, dataAccessor: (stats) => stats.sequences?.pullbackPostMove?.up },
  { id: 'pullbackPostMoveDown', label: '↳ Down', level: 1, dataAccessor: (stats) => stats.sequences?.pullbackPostMove?.down },

  { id: 'pullbackPostPullback', label: 'Post: Pullback after Pullback', level: 0, dataAccessor: (stats) => stats.sequences?.pullbackPostPullback },
  { id: 'pullbackPostPullbackUp', label: '↳ Up', level: 1, dataAccessor: (stats) => stats.sequences?.pullbackPostPullback?.up },
  { id: 'pullbackPostPullbackDown', label: '↳ Down', level: 1, dataAccessor: (stats) => stats.sequences?.pullbackPostPullback?.down },

  { id: 'movePostMove', label: 'Post: Move after Move', level: 0, dataAccessor: (stats) => stats.sequences?.movePostMove },
  { id: 'movePostMoveUp', label: '↳ Up', level: 1, dataAccessor: (stats) => stats.sequences?.movePostMove?.up },
  { id: 'movePostMoveDown', label: '↳ Down', level: 1, dataAccessor: (stats) => stats.sequences?.movePostMove?.down },
];

// **********************************
// INDICATORS
// **********************************

const StrategyExpectancyTable = ({ results }) => {
  // Внутренние хелперы и логика для сортировки (остаются без изменений)
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('depth');

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedResults = useMemo(() => {
    const comparator = (a, b) => {
      if (b.expectancy[orderBy] < a.expectancy[orderBy]) return -1;
      if (b.expectancy[orderBy] > a.expectancy[orderBy]) return 1;
      return 0;
    };
    const stabilizedThis = results.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
      const orderValue = comparator(a[0], b[0]);
      if (orderValue !== 0) return order === 'desc' ? orderValue : -orderValue;
      return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
  }, [order, orderBy, results]);

  // Определяем заголовки для новой, правильной таблицы
  const headCells = [
    { id: 'title', label: 'Strategy', numeric: false },
    { id: 'depth', label: 'Depth', numeric: false },
    { id: 'netExpectancy', label: 'Expectancy (Net %)', numeric: true },
    { id: 'rrRatio', label: 'R/R Ratio', numeric: true },
    { id: 'winRate', label: 'Win Rate (%)', numeric: true },
    { id: 'avgWin', label: 'Avg Win (%)', numeric: true },
    { id: 'avgLoss', label: 'Avg Loss (%)', numeric: true },
    { id: 'enter', label: 'Enter (%) / xR', numeric: true },
    { id: 'sl', label: 'SL (%)', numeric: true },
    { id: 'tsl', label: 'TSL (%)', numeric: true },
    { id: 'tp1', label: 'TP 1 (%) / xR', numeric: true },
    { id: 'tp2', label: 'TP 2 (%) / xR', numeric: true }
  ];

  return (
    <Paper variant="outlined">
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {headCells.map((headCell) => (
                <TableCell
                  key={headCell.id}
                  align={headCell.numeric ? 'right' : 'left'}
                  sortDirection={orderBy === headCell.id ? order : false}
                >
                  <TableSortLabel
                    active={orderBy === headCell.id}
                    direction={orderBy === headCell.id ? order : 'asc'}
                    onClick={() => handleRequestSort(headCell.id)}
                  >
                    {headCell.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedResults.map((result) => {
              const { expectancy, title } = result;
              // --- ДОБАВЛЕН РАСЧЕТ R/R ---
              const rrRatio = expectancy.avgLoss !== 0 ? expectancy.avgWin / expectancy.avgLoss : Infinity;

              return (
                <TableRow hover key={title}>
                  <TableCell component="th" scope="row">
                    {title}
                  </TableCell>
                  <TableCell component="th" scope="row">
                    {expectancy.depth}
                  </TableCell>
                  <TableCell align="right" sx={{
                    fontWeight: 'bold',
                    color: expectancy.netExpectancy > 0 ? 'success.main' : 'error.main'
                  }}>
                    {formatValue(expectancy.netExpectancy, 3, true)}%
                  </TableCell>
                  <TableCell align="right">
                    {isFinite(rrRatio) ? formatValue(rrRatio, 2) : 'N/A'}
                  </TableCell>
                  <TableCell align="right">{(expectancy.winRate * 100).toFixed(1)}%</TableCell>
                  <TableCell align="right" sx={{
                    color: expectancy.avgWin > 0 ? 'success.dark' : 'error.dark'
                  }}>
                    {formatValue(expectancy.avgWin, 2, true)}%
                  </TableCell>
                  <TableCell align="right" sx={{ color: 'error.dark' }}>
                    -{formatValue(expectancy.avgLoss, 2, true)}%
                  </TableCell>
                  <TableCell align="right">
                    {formatValue(expectancy.enter, 2, true)}% / {formatValue(expectancy.enterRatio, 2)}
                  </TableCell>
                  <TableCell align="right" sx={{
                    color: 'error.dark'
                  }}>
                    {formatValue(expectancy.stopLoss, 2, true)}%
                  </TableCell>
                  <TableCell align="right" sx={{
                    color: 'error.dark'
                  }}>
                    {formatValue(expectancy.smallTSL, 2, true)}% / {formatValue(expectancy.bigTSL, 2, true)}%
                  </TableCell>
                  <TableCell align="right" sx={{
                    color: expectancy.target1 > 0 ? 'success.dark' : 'error.dark'
                  }}>
                    {formatValue(expectancy.target1, 2, true)}% / {formatValue(expectancy.targetRatio1, 2)}

                  </TableCell>
                  <TableCell align="right" sx={{
                    color: expectancy.target2 > 0 ? 'success.dark' : 'error.dark'
                  }}>
                    {formatValue(expectancy.target2, 2, true)}% / {formatValue(expectancy.targetRatio2, 2)}
                  </TableCell>

                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

const ClusterExpectancyTable = ({ results }) => {
  // Внутренние хелперы и логика для сортировки (остаются без изменений)
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('title');

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedResults = useMemo(() => {
    const comparator = (a, b) => {
      if (b.expectancy[orderBy] < a.expectancy[orderBy]) return -1;
      if (b.expectancy[orderBy] > a.expectancy[orderBy]) return 1;
      return 0;
    };
    const stabilizedThis = results.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
      const orderValue = comparator(a[0], b[0]);
      if (orderValue !== 0) return order === 'desc' ? orderValue : -orderValue;
      return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
  }, [order, orderBy, results]);

  // Определяем заголовки для новой, правильной таблицы
  const headCells = [
    { id: 'title', label: 'Strategy', numeric: false },
    { id: 'prob', label: 'Probability', numeric: false },
    { id: 'netExpectancy', label: 'Expectancy (Net %)', numeric: true },
    { id: 'rrRatio', label: 'R/R Ratio', numeric: true },
    { id: 'winRate', label: 'Win Rate (%)', numeric: true },
    { id: 'avgWin', label: 'Avg Win (%)', numeric: true },
    { id: 'avgLoss', label: 'Avg Loss (%)', numeric: true },
    { id: 'enter', label: 'Enter (%)', numeric: true },
    { id: 'sl', label: 'SL (%)', numeric: true },
    { id: 'tsl', label: 'TSL (%)', numeric: true },
    { id: 'tp1', label: 'TP 1 (%)', numeric: true },
    { id: 'tp2', label: 'TP 2 (%)', numeric: true }
  ];

  return (
    <Paper variant="outlined">
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {headCells.map((headCell) => (
                <TableCell
                  key={headCell.id}
                  align={headCell.numeric ? 'right' : 'left'}
                  sortDirection={orderBy === headCell.id ? order : false}
                >
                  <TableSortLabel
                    active={orderBy === headCell.id}
                    direction={orderBy === headCell.id ? order : 'asc'}
                    onClick={() => handleRequestSort(headCell.id)}
                  >
                    {headCell.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedResults.map((result) => {
              const { expectancy, title } = result;
              // --- ДОБАВЛЕН РАСЧЕТ R/R ---
              const rrRatio = expectancy.avgLoss !== 0 ? expectancy.avgWin / expectancy.avgLoss : Infinity;

              return (
                <TableRow hover key={title}>
                  <TableCell component="th" scope="row">
                    {title}
                  </TableCell>
                  <TableCell component="th" scope="row">
                    {formatValue(expectancy.prob, 3, true)}%
                  </TableCell>
                  <TableCell align="right" sx={{
                    fontWeight: 'bold',
                    color: expectancy.netExpectancy > 0 ? 'success.main' : 'error.main'
                  }}>
                    {formatValue(expectancy.netExpectancy, 3, true)}%
                  </TableCell>
                  <TableCell align="right">
                    {isFinite(rrRatio) ? formatValue(rrRatio, 2) : 'N/A'}
                  </TableCell>
                  <TableCell align="right">{(expectancy.winRate * 100).toFixed(1)}%</TableCell>
                  <TableCell align="right" sx={{
                    color: expectancy.avgWin > 0 ? 'success.dark' : 'error.dark'
                  }}>
                    {formatValue(expectancy.avgWin, 2, true)}%
                  </TableCell>
                  <TableCell align="right" sx={{ color: 'error.dark' }}>
                    -{formatValue(expectancy.avgLoss, 2, true)}%
                  </TableCell>
                  <TableCell align="right">
                    {formatValue(expectancy.enter, 2, true)}%
                  </TableCell>
                  <TableCell align="right" sx={{
                    color: 'error.dark'
                  }}>
                    {formatValue(expectancy.stopLoss, 2, true)}%
                  </TableCell>
                  <TableCell align="right" sx={{
                    color: 'error.dark'
                  }}>
                    {formatValue(expectancy.smallTSL, 2, true)}% / {formatValue(expectancy.bigTSL, 2, true)}%
                  </TableCell>
                  <TableCell align="right" sx={{
                    color: expectancy.target1 > 0 ? 'success.dark' : 'error.dark'
                  }}>
                    {formatValue(expectancy.target1, 2, true)}%

                  </TableCell>
                  <TableCell align="right" sx={{
                    color: expectancy.target2 > 0 ? 'success.dark' : 'error.dark'
                  }}>
                    {formatValue(expectancy.target2, 2, true)}%
                  </TableCell>

                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

const StrategyDashboard = (props) => {
  const { waves, stats, sequenceStats, clusterStats, clusterOutcomes } = props
  // inputs
  const [stopMultiplier, setStopMultiplier] = useState(1);
  const [takeShift, setTakeShift] = useState(0);
  // calc

  // data
  const [configs, setConfigs] = useState([]);
  const [clustersPaths, setClustersPaths] = useState(null);
  const [clusterConfigs, setClustersConfigs] = useState(null);
  const [groupedOutcomes, setGroupedOutcomes] = useState(null);

  const handleMultiplierChange = (event) => {
    let value = parseFloat(event.target.value);
    if (isNaN(value)) {
      value = 1;
    }
    if (value < 1) value = 1;
    if (value > 3) value = 3;
    setStopMultiplier(value);
  };

  const handleTakeShiftChange = (event) => {
    let value = parseFloat(event.target.value);
    if (isNaN(value)) {
      value = 0;
    }
    if (value < -3) value = -3;
    if (value > 3) value = 3;
    setTakeShift(value);
  };



  useEffect(() => {
    if (waves.length >= 3) {
      const lastWave = waves[waves.length - 1];
      const prevWave = waves[waves.length - 2];

      const configs = strategiesConfig.filter(config => config.setupWaveType === lastWave.type
        && (config.setupWaveDirection === lastWave.direction || config.setupWaveDirection === 'any')
        && (config.prevWaveType === prevWave.type || config.prevWaveType === 'any'));

      setConfigs(configs);

      const clustersPaths = getClustersPaths(waves)
      setClustersPaths(clustersPaths)

      setClustersConfigs({
        configs,
        clustersPaths,
      })
    }
  }, [waves])

  useEffect(() => {
    if (waves?.length > 3) {
      const lastWave = waves[waves.length - 1];
      const prevWave = waves[waves.length - 2];

      const pathsCurrent = [
        `${lastWave.type}.${prevWave.type}${prevWave.direction.charAt(0).toUpperCase() + prevWave.direction.slice(1)}`,
        `${lastWave.type}.${prevWave.type}`,
        `${lastWave.type}.${prevWave.direction}`
        // `${lastWave.type}.any`,
      ]
      const resultsPercent = getSpecificClusterStats(waves, pathsCurrent, 'deltaPercent')
      const outcomePercent = getClustersOutcome(waves, pathsCurrent, resultsPercent.clusters, 'deltaPercent', 'direct')
      resultsPercent.clusters = outcomePercent
      console.log('current getSpecificClusterStats', resultsPercent)

      // const resultsRatio = getSpecificClusterStats(waves, pathsCurrent, 'deltaPercent', 'ratio')
    }
  }, [waves])

  useEffect(() => {
    if (clusterStats && clustersPaths) {
      const getDeltaPercent = (path) => path.split('.').reduce((o, k) => o?.[k], clusterStats.deltaPercent);
      const getDeltaRatioPrev = (path) => path.split('.').reduce((o, k) => o?.[k], clusterStats.deltaRatioPrev);

      const results = {}

      for (const key in clustersPaths) {
        if (key === 'currentType') {
          results[key] = clustersPaths[key]
          continue
        }

        const variants = []

        for (const p of clustersPaths[key]) {
          const infoPercent = getDeltaPercent(p)
          const infoRatio = getDeltaRatioPrev(p)
          variants.push({
            path: p,
            deltaPercent: infoPercent,
            deltaRatioPrev: infoRatio,
          })
        }

        results[key] = variants
      }

      console.log('current clusterStats', results)
    }
  }, [clusterStats, clustersPaths])

  useEffect(() => {
    if (clusterOutcomes && clustersPaths) {
      const groupedOutcomes = getGroupedOutcomes(clustersPaths, clusterOutcomes)
      setGroupedOutcomes(groupedOutcomes)
    }
  }, [clusterOutcomes, clustersPaths])



  const strategyResults = useMemo(() => {
    return configs
      .map(config => ({
        title: config.title,
        expectancy: calculateFadeExpectancy(stats, config, {
          commission: (marketFee * 2) * (1 - cashbackRate), // 0.002,
          stopMultiplier,
          takeShift,
          averagingMode: 'avg'
        })
      }))
      .filter(result => result?.expectancy !== null); // Убираем те, что не удалось рассчитать
  }, [stats, stopMultiplier, takeShift, configs]);

  const blendedExpectancy = useMemo(() => {
    return getBlendedExpectancy(strategyResults)
  }, [strategyResults]);

  const joinedResults = useMemo(() => {
    // console.log('blendedExpectancy', blendedExpectancy)
    console.log('strategyResults', strategyResults)

    return blendedExpectancy ? [{
      title: 'Bayesian Adjusted',
      expectancy: blendedExpectancy,
    }, ...strategyResults] : strategyResults
  }, [strategyResults, blendedExpectancy]);


  const clusterResults = useMemo(() => {
    if (groupedOutcomes && stats && waves.length > 3) {

      const expectancy = calculateClusterFadeExpectancy(stats, groupedOutcomes, sequenceStats, waves, {
        commission: (marketFee * 2) * (1 - cashbackRate), // 0.002,
        averagingMode: 'avg',
        stopMultiplier,
        takeShift,
        clusterPathIndex: 3,
        enterDistMultIndex: 1,
        exitDistMultIndex: 1,
      })
      console.log('clusterStrategyResults', expectancy)


      const prev = expectancy.prevRatio.map((c, i) => ({
        title: `Previous ${i}`,
        expectancy: c
      }))

      const currentPullback = expectancy.currentPullbackRatio.map((c, i) => ({
        title: `Current Pullback ${i}`,
        expectancy: c
      }))
      const currentMove = expectancy.currentMoveRatio.map((c, i) => ({
        title: `Current Move ${i}`,
        expectancy: c
      }))

      const prepared = [...prev, ...currentPullback, ...currentMove]

      return prepared
    }

    return null
  }, [stats, groupedOutcomes, sequenceStats, stopMultiplier, takeShift, waves]);


  const lastWave = waves[waves.length - 1];
  const prevWave = waves[waves.length - 2];

  if (strategyResults.length === 0 || !lastWave || !prevWave) {
    return <Typography sx={{ p: 2, textAlign: 'center' }}>Not enouth data</Typography>;
  }


  return (
    <Box sx={{ mb: 1 }}>
      <Paper variant="outlined" sx={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'flex-start',
        alignItems: 'center',
        p: 2,
        mb: 2,
        gap: 1,
      }}>

        {/* <VolatilityMetrics stats={stats} /> */}
        <SequencePrediction sequenceStats={sequenceStats} waves={waves} />
        <SequenceAnalyze simpleStats={stats} waves={waves} />

        <Box sx={{ mr: 5 }}>
          <Typography sx={{ textAlign: 'center' }}>Current: {lastWave.type} {lastWave.direction} after {prevWave.type}</Typography>
          <Typography sx={{ textAlign: 'center' }}>Blended Expectancy: {formatValue(blendedExpectancy?.netExpectancy, 3, true)}%</Typography>
        </Box>

        <FormControl component="fieldset" sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 1,
        }}>
          <TextField
            label="SL multiplier"
            type="number"
            size="small"
            value={stopMultiplier}
            onChange={handleMultiplierChange}
            inputProps={{
              step: 1,
              min: 1,
              max: 3,
            }}
            sx={{ minWidth: '100px' }}
          />
          <TextField
            label="TP shift"
            type="number"
            size="small"
            value={takeShift}
            onChange={handleTakeShiftChange}
            inputProps={{
              step: 1,
              min: -3,
              max: 3,
            }}
            sx={{ minWidth: '100px' }}
          />
        </FormControl>
      </Paper>

      <Box sx={{ mb: 1 }}>
        {
          clusterResults?.length
            ? <ClusterExpectancyTable results={clusterResults} />
            : <Typography sx={{ p: 2, textAlign: 'center' }}>Not enouth data</Typography>
        }
      </Box>
      <Box sx={{ mb: 1 }}>
        {
          strategyResults?.length
            ? <StrategyExpectancyTable results={joinedResults} />
            : <Typography sx={{ p: 2, textAlign: 'center' }}>Not enouth data</Typography>
        }
      </Box>
    </Box>
  );
}

export const CurrentWaveState = () => {
  const optimalWaves = useRecoilValue(optimalWavesState);
  const simpleStats = useRecoilValue(simpleStatsState);
  const sequenceStats = useRecoilValue(sequenceStatsState);
  const waveClusterStats = useRecoilValue(waveClusterStatsState)
  const waveClusterOutcomes = useRecoilValue(waveClusterOutcomesState)

  if (simpleStats === null || optimalWaves === null) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 1 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Calculating...</Typography>
      </Box>
    );
  }
  if (!simpleStats.overall?.count) {
    return <Typography sx={{ p: 1, textAlign: 'center' }}>No Data Available</Typography>;
  }
  return (
    <Box sx={{ p: 1 }}>
      <StrategyDashboard
        waves={optimalWaves}
        stats={simpleStats}
        sequenceStats={sequenceStats}
        clusterStats={waveClusterStats}
        clusterOutcomes={waveClusterOutcomes}
      />
    </Box>
  );
};

export default CurrentWaveState;


function VolatilityMetrics(props) {
  const { stats } = props

  const [latestChanges, setLatestChanges] = useState({});

  useEffect(() => {
    if (stats) {
      const volatilityOverall = stats.overall.deltaPercent.ema.avg / stats.overall.deltaPercent.avg
      const volatilityMove = stats.move.deltaPercent.ema.avg / stats.move.deltaPercent.avg
      const volatilityPullback = stats.pullback.deltaPercent.ema.avg / stats.pullback.deltaPercent.avg
      const volatilityOverallRatio = stats.overall.deltaRatioPrev.ema.avg / stats.overall.deltaRatioPrev.avg
      const volatilityMoveRatio = stats.move.deltaRatioPrev.ema.avg / stats.move.deltaRatioPrev.avg
      const volatilityPullbackRatio = stats.pullback.deltaRatioPrev.ema.avg / stats.pullback.deltaRatioPrev.avg
      const volatilityOverallMed = stats.overall.deltaPercent.ema.median / stats.overall.deltaPercent.median
      const volatilityMoveMed = stats.move.deltaPercent.ema.median / stats.move.deltaPercent.median
      const volatilityPullbackMed = stats.pullback.deltaPercent.ema.median / stats.pullback.deltaPercent.median
      const volatilityOverallRatioMed = stats.overall.deltaRatioPrev.ema.median / stats.overall.deltaRatioPrev.median
      const volatilityMoveRatioMed = stats.move.deltaRatioPrev.ema.median / stats.move.deltaRatioPrev.median
      const volatilityPullbackRatioMed = stats.pullback.deltaRatioPrev.ema.median / stats.pullback.deltaRatioPrev.median


      const latest = {
        volatilityOverall,
        volatilityMove,
        volatilityPullback,
        volatilityOverallRatio,
        volatilityMoveRatio,
        volatilityPullbackRatio,
        volatilityOverallMed,
        volatilityMoveMed,
        volatilityPullbackMed,
        volatilityOverallRatioMed,
        volatilityMoveRatioMed,
        volatilityPullbackRatioMed,
      }

      setLatestChanges(latest);
    }
  }, [stats])

  return (<>
    <Box sx={{ mr: 5 }}>
      <Typography sx={{ textAlign: 'center' }}>Volatility Mult Overall: {formatValue(latestChanges?.volatilityOverall, 2, true)}%</Typography>
      <Typography sx={{ textAlign: 'center' }}>Volatility Mult Move: {formatValue(latestChanges?.volatilityMove, 2, true)}%</Typography>
      <Typography sx={{ textAlign: 'center' }}>Volatility Mult Pullback: {formatValue(latestChanges?.volatilityPullback, 2, true)}%</Typography>
    </Box>

    <Box sx={{ mr: 5 }}>
      <Typography sx={{ textAlign: 'center' }}>Volatility Mult Overall Ratio: {formatValue(latestChanges?.volatilityOverallRatio, 2, true)}%</Typography>
      <Typography sx={{ textAlign: 'center' }}>Volatility Mult Move Ratio: {formatValue(latestChanges?.volatilityMoveRatio, 2, true)}%</Typography>
      <Typography sx={{ textAlign: 'center' }}>Volatility Mult Pullback Ratio: {formatValue(latestChanges?.volatilityPullbackRatio, 2, true)}%</Typography>
    </Box>

    <Box sx={{ mr: 5 }}>
      <Typography sx={{ textAlign: 'center' }}>Volatility Mult Overall Med: {formatValue(latestChanges?.volatilityOverallMed, 2, true)}%</Typography>
      <Typography sx={{ textAlign: 'center' }}>Volatility Mult Move Med: {formatValue(latestChanges?.volatilityMoveMed, 2, true)}%</Typography>
      <Typography sx={{ textAlign: 'center' }}>Volatility Mult Pullback Med: {formatValue(latestChanges?.volatilityPullbackMed, 2, true)}%</Typography>
    </Box>

    <Box sx={{ mr: 5 }}>
      <Typography sx={{ textAlign: 'center' }}>Volatility Mult Overall Ratio Med: {formatValue(latestChanges?.volatilityOverallRatioMed, 2, true)}%</Typography>
      <Typography sx={{ textAlign: 'center' }}>Volatility Mult Move Ratio Med: {formatValue(latestChanges?.volatilityMoveRatioMed, 2, true)}%</Typography>
      <Typography sx={{ textAlign: 'center' }}>Volatility Mult Pullback Ratio Med: {formatValue(latestChanges?.volatilityPullbackRatioMed, 2, true)}%</Typography>
    </Box>

  </>)
}


function SequencePrediction(props) {
  const { sequenceStats, waves } = props

  const [predicted, setPredicted] = useState(null);

  useEffect(() => {
    if (waves?.length && sequenceStats) {

      const prevSequence = predictSequence(waves.slice(0, -1), sequenceStats);
      const p1 = prevSequence?.[1]?.prediction[0]
      const p2 = prevSequence?.[2]?.prediction[0]

      const currentSequence = predictSequence(waves, sequenceStats);
      const c1 = currentSequence?.[1]?.prediction[0]
      const c2 = currentSequence?.[2]?.prediction[0]

      setPredicted({
        p1, p2,
        c1, c2,
      });
    }
  }, [sequenceStats, waves])

  if (!predicted) return null

  return (<>
    <Box sx={{ mr: 5 }}>
      <Typography sx={{ textAlign: 'center' }}>
        P2: {predicted?.p1?.nextWave} {formatValue(predicted?.p1?.probability, 2, true)}%
      </Typography>
      <Typography sx={{ textAlign: 'center' }}>
        P3: {predicted?.p2?.nextWave} {formatValue(predicted?.p2?.probability, 2, true)}%
      </Typography>
    </Box>
    <Box sx={{ mr: 5 }}>
      <Typography sx={{ textAlign: 'center' }}>
        C2: {predicted?.c1?.nextWave} {formatValue(predicted?.c1?.probability, 2, true)}%
      </Typography>
      <Typography sx={{ textAlign: 'center' }}>
        C3: {predicted?.c2?.nextWave} {formatValue(predicted?.c2?.probability, 2, true)}%
      </Typography>
    </Box>
  </>)
}

function SequenceAnalyze(props) {
  const { simpleStats, waves } = props

  const [predicted, setPredicted] = useState(null);

  useEffect(() => {
    if (waves?.length > 2 && simpleStats) {
      const myConfig = {
        // Список метрик, по которым будет строиться оценка
        metrics: [
          {
            name: 'deltaPercent_avg',
            currentValueAccessor: (current) => current.deltaPercent,
            historicalPath: 'deltaPercent.avg',
            weight: 0.2,
          },
          {
            name: 'deltaRatioPrev_avg',
            currentValueAccessor: (current, prev) => current.deltaPercent / prev.deltaPercent,
            historicalPath: 'deltaRatioPrev.avg',
            weight: 0.5,
          },
          {
            name: 'totalVolume_avg',
            currentValueAccessor: (current) => current.totalVolume,
            historicalPath: 'totalVolume.avg',
            weight: 0.3,
          },
          {
            name: 'effortRatio_avg',
            currentValueAccessor: (current) => (current.delta !== 0 ? current.totalVolume / Math.abs(current.deltaPercent) : 0),
            historicalPath: 'effortRatio.avg',
            weight: 0.3,
          },
        ]
      };

      const prevAnalysis = getAnalysis(
        waves.slice(0, -1),
        simpleStats,
        myConfig
      );
      const currentAnalysis = getAnalysis(
        waves,
        simpleStats,
        myConfig
      );

      console.log('getPrediction', prevAnalysis, currentAnalysis);


      const imbalances = findStrongestImbalances(simpleStats, comparisonConfig)
      console.log('imbalances', imbalances);

      setPredicted({
        prev: prevAnalysis,
        last: currentAnalysis,
      })
    }

  }, [waves, simpleStats])

  if (!predicted) return null

  return (<>
    <Box sx={{ mr: 5 }}>
      <Typography sx={{ textAlign: 'center' }}>
        PC: {formatValue(predicted?.prev?.current?.scoreRatio, 3)}
      </Typography>
      <Typography sx={{ textAlign: 'center' }}>
        PN: {formatValue(predicted?.prev?.next?.scoreRatio, 3)}
      </Typography>
    </Box>
    <Box sx={{ mr: 5 }}>
      <Typography sx={{ textAlign: 'center' }}>
        LC: {formatValue(predicted?.last?.current?.scoreRatio, 3)}
      </Typography>
      <Typography sx={{ textAlign: 'center' }}>
        LN: {formatValue(predicted?.last?.next?.scoreRatio, 3)}
      </Typography>
    </Box>
  </>)
}

/**
 * Извлекает значение из вложенного объекта по строковому пути.
 */
function queryStats(obj, path) {
  if (!obj || !path) return null;
  try {
    return path.split('.').reduce((current, key) => current?.[key], obj) ?? null;
  } catch (error) {
    return null;
  }
}

/**
 * Проводит комплексный анализ.
 * @param {Array<object>} waves - Массив волн.
 * @param {object} stats - Объект статистики.
 * @param {object} config - Конфигурационный объект.
 * @returns {object} - Объект с результатами анализа и прогноза.
 */
function getAnalysis(waves, stats, config) {
  const metrics = config.metrics;
  const trendCurrent = waves[waves.length - 1];
  const trendPrev = waves.length > 1 ? waves[waves.length - 2] : null;

  // --- ЧАСТЬ 1: АНАЛИЗ ТЕКУЩЕЙ ВОЛНЫ (оценка по "расстоянию" до прототипа) ---
  let current = { moveScore: 0, pullbackScore: 0, details: {} };
  if (trendPrev) {
    const direction = trendCurrent.direction;
    const prevTypeCap = trendPrev.type.charAt(0).toUpperCase() + trendPrev.type.slice(1);
    const movePath = `sequences.movePost${prevTypeCap}.${direction}`;
    const pullbackPath = `sequences.pullbackPost${prevTypeCap}.${direction}`;
    metrics.forEach(metric => {
      const currentValue = metric.currentValueAccessor(trendCurrent, trendPrev);
      const historicalForMove = queryStats(stats, `${movePath}.${metric.historicalPath}`);
      const historicalForPullback = queryStats(stats, `${pullbackPath}.${metric.historicalPath}`);
      const distToMove = historicalForMove ? Math.abs(currentValue - historicalForMove) / historicalForMove : 0;
      const distToPullback = historicalForPullback ? Math.abs(currentValue - historicalForPullback) / historicalForPullback : 0;
      const moveFit = 1 / (1 + distToMove);
      const pullbackFit = 1 / (1 + distToPullback);
      current.moveScore += moveFit * metric.weight;
      current.pullbackScore += pullbackFit * metric.weight;
      current.details[metric.name] = { currentValue, historicalForMove, historicalForPullback, moveFit, pullbackFit };
    });
  }

  // --- ЧАСТЬ 2: ПРОГНОЗ СЛЕДУЮЩЕЙ ВОЛНЫ (Логарифмическая оценка) ---
  const lastCompletedWave = trendCurrent;
  const nextMovePath = `sequences.${lastCompletedWave.type}PreMove`;
  const nextPullbackPath = `sequences.${lastCompletedWave.type}PrePullback`;

  let next = { details: {} };
  let weightedLogRatioSum = 0;
  let totalWeight = 0;

  metrics.forEach(metric => {
    const moveValue = queryStats(stats, `${nextMovePath}.${metric.historicalPath}`) || 0;
    const pullbackValue = queryStats(stats, `${nextPullbackPath}.${metric.historicalPath}`) || 0;

    if (pullbackValue > 0 && moveValue > 0) {
      const ratio = moveValue / pullbackValue;
      const logRatio = Math.log(ratio);

      weightedLogRatioSum += logRatio * metric.weight;
      totalWeight += metric.weight;

      next.details[metric.name] = { moveValue, pullbackValue, ratio, logRatio };
    }
  });

  const avgLogRatio = totalWeight > 0 ? weightedLogRatioSum / totalWeight : 0;
  const finalScoreRatio = Math.exp(avgLogRatio);

  // --- Расчет итоговых соотношений ---
  current.scoreRatio = current.pullbackScore > 0 ? current.moveScore / current.pullbackScore : Infinity;
  next.scoreRatio = finalScoreRatio;

  return { current, next };
}

export const comparisonConfig = {
  // --- СПИСОК МЕТРИК ДЛЯ СРАВНЕНИЯ ---
  metrics: [
    { name: 'Count', accessor: (d) => d?.count },
    { name: 'Avg xRatio', accessor: (d) => d?.deltaRatioPrev?.avg },
    { name: 'Avg Delta (%)', accessor: (d) => d?.deltaPercent?.avg, isPercent: true },
    { name: 'Med Delta (%)', accessor: (d) => d?.deltaPercent?.median, isPercent: true },
    { name: 'Avg Volume', accessor: (d) => d?.totalVolume?.avg },
    { name: 'Avg Effort', accessor: (d) => d?.effortRatio?.avg },
  ],

  // --- СПИСОК СРАВНЕНИЙ ---
  comparisons: [
    // =======================================================
    // ОБЩИЕ СРАВНЕНИЯ (БЕЗ РАЗДЕЛЕНИЯ ПО НАПРАВЛЕНИЮ)
    // =======================================================
    {
      title: 'Base',
      category: 'base',
      pathA: 'move', titleA: 'Move', // Исправлено
      pathB: 'pullback', titleB: 'Pullback' // Исправлено
    },
    {
      title: 'Pre: Move before',
      category: 'movePre',
      pathA: 'sequences.movePreMove', titleA: 'Move -> Move',
      pathB: 'sequences.movePrePullback', titleB: 'Move -> Pullback'
    },
    {
      title: 'Post: Move after',
      category: 'movePost',
      pathA: 'sequences.movePostMove', titleA: 'Move -> Move',
      pathB: 'sequences.movePostPullback', titleB: 'Pullback -> Move'
    },
    {
      title: 'Pre: Pullback before',
      category: 'pullbackPre',
      pathA: 'sequences.pullbackPreMove', titleA: 'Pullback -> Move',
      pathB: 'sequences.pullbackPrePullback', titleB: 'Pullback -> Pullback'
    },
    {
      title: 'Post: Pullback after',
      category: 'pullbackPost',
      pathA: 'sequences.pullbackPostMove', titleA: 'Move -> Pullback',
      pathB: 'sequences.pullbackPostPullback', titleB: 'Pullback -> Pullback'
    },

    // =======================================================
    // ВОСХОДЯЩИЙ ТРЕНД (UP)
    // =======================================================
    {
      title: 'Base (Up)',
      category: 'base',
      pathA: 'move.up', titleA: 'Move Up',
      pathB: 'pullback.up', titleB: 'Pullback Up'
    },
    {
      title: 'Pre: Move before (Up)',
      category: 'movePre',
      pathA: 'sequences.movePreMove.up', titleA: 'Move Up -> Move Up',
      pathB: 'sequences.movePrePullback.up', titleB: 'Move Up -> Pullback Up'
    },
    {
      title: 'Pre: Pullback before (Up)',
      category: 'pullbackPre',
      pathA: 'sequences.pullbackPreMove.up', titleA: 'Pullback Up -> Move Up',
      pathB: 'sequences.pullbackPrePullback.up', titleB: 'Pullback Up -> Pullback Up'
    },
    {
      title: 'Post: Move after (Up)',
      category: 'movePost',
      pathA: 'sequences.movePostMove.up', titleA: 'Move Up -> Move Up',
      pathB: 'sequences.movePostPullback.up', titleB: 'Pullback Up -> Move Up'
    },
    {
      title: 'Post: Pullback after (Up)',
      category: 'pullbackPost',
      pathA: 'sequences.pullbackPostMove.up', titleA: 'Move Up -> Pullback Up',
      pathB: 'sequences.pullbackPostPullback.up', titleB: 'Pullback Up -> Pullback Up'
    },

    // =======================================================
    // НИСХОДЯЩИЙ ТРЕНД (DOWN)
    // =======================================================
    {
      title: 'Base (Down)',
      category: 'base',
      pathA: 'move.down', titleA: 'Move Down',
      pathB: 'pullback.down', titleB: 'Pullback Down'
    },
    {
      title: 'Pre: Move before (Down)',
      category: 'movePre',
      pathA: 'sequences.movePreMove.down', titleA: 'Move Down -> Move Down',
      pathB: 'sequences.movePrePullback.down', titleB: 'Move Down -> Pullback Down'
    },
    {
      title: 'Pre: Pullback before (Down)',
      category: 'pullbackPre',
      pathA: 'sequences.pullbackPreMove.down', titleA: 'Pullback Down -> Move Down',
      pathB: 'sequences.pullbackPrePullback.down', titleB: 'Pullback Down -> Pullback Down'
    },
    {
      title: 'Post: Move after (Down)',
      category: 'movePost',
      pathA: 'sequences.movePostMove.down', titleA: 'Move Down -> Move Down',
      pathB: 'sequences.movePostPullback.down', titleB: 'Pullback Down -> Move Down'
    },
    {
      title: 'Post: Pullback after (Down)',
      category: 'pullbackPost',
      pathA: 'sequences.pullbackPostMove.down', titleA: 'Move Down -> Pullback Down',
      pathB: 'sequences.pullbackPostPullback.down', titleB: 'Pullback Down -> Pullback Down'
    },

    // =======================================================
    // СРАВНЕНИЕ НАПРАВЛЕНИЙ (UP vs DOWN)
    // =======================================================
    {
      title: 'Directional: Move',
      category: 'move',
      pathA: 'move.up', titleA: 'Move Up',
      pathB: 'move.down', titleB: 'Move Down'
    },
    {
      title: 'Directional: Pullback',
      category: 'pullback',
      pathA: 'pullback.up', titleA: 'Pullback Up',
      pathB: 'pullback.down', titleB: 'Pullback Down'
    },
    {
      title: 'Directional: Move -> Move',
      category: 'movePreMove',
      pathA: 'sequences.movePreMove.up', titleA: 'Move Up -> Move Up',
      pathB: 'sequences.movePreMove.down', titleB: 'Move Down -> Move Down'
    },
    {
      title: 'Directional: Pullback -> Pullback',
      category: 'pullbackPrePullback',
      pathA: 'sequences.pullbackPrePullback.up', titleA: 'Pullback Up -> Pullback Up',
      pathB: 'sequences.pullbackPrePullback.down', titleB: 'Pullback Down -> Pullback Down'
    },
  ]
};

/**
 * Находит и ранжирует самые сильные дисбалансы в статистике на основе конфига.
 * @param {object} stats - Полный объект статистики.
 * @param {object} config - Объект comparisonConfig.
 * @returns {Array<object>} - Отсортированный массив дисбалансов.
 */
function findStrongestImbalances(stats, config) {
  const { comparisons, metrics } = config;
  const allImbalances = [];

  for (const comp of comparisons) {
    const dataA = queryStats(stats, comp.pathA);
    const dataB = queryStats(stats, comp.pathB);

    if (!dataA || !dataB) continue;

    let totalImbalanceScore = 0;
    const details = []; // ИЗМЕНЕНО: Инициализируем как массив

    metrics.forEach(metric => {
      const valueA = metric.accessor(dataA);
      const valueB = metric.accessor(dataB);

      if (typeof valueA !== 'number' || typeof valueB !== 'number') return;

      const ratio = valueB > 0 ? valueA / valueB : Infinity;
      const imbalance = (ratio > 0 && isFinite(ratio)) ? Math.abs(Math.log(ratio)) : 0;

      totalImbalanceScore += imbalance;

      // ИЗМЕНЕНО: Добавляем объект в массив
      details.push({
        name: metric.name,
        valueA,
        valueB,
        ratio,
        imbalance,
      });
    });

    // ИЗМЕНЕНО: Сортируем массив details по силе дисбаланса каждой метрики
    details.sort((a, b) => b.imbalance - a.imbalance);

    allImbalances.push({
      imbalanceScore: totalImbalanceScore,
      title: comp.title,
      titleA: comp.titleA,
      titleB: comp.titleB,
      category: comp.category,
      comparison: `${comp.titleA} (A) vs ${comp.titleB} (B)`,
      details, // Теперь это отсортированный массив
    });
  }

  allImbalances.sort((a, b) => b.imbalanceScore - a.imbalanceScore);

  return allImbalances
}